/**
 * What a rejected field says, in one vocabulary — **every** zod constraint takes its
 * message from here rather than spelling one out at the `.min()`, so that the same rule
 * reads the same way in every form.
 *
 * A constraint left without a message falls back to zod's developer text (`Too small:
 * expected string to have >=4 characters`), which is shown to the user as it is. Add a
 * builder here when the wording is new; reuse one otherwise.
 */
export const atLeastChars = (n: number) =>
  `At least ${n} character${n === 1 ? '' : 's'}`

export const atMostChars = (n: number) => `At most ${n} characters`

/** For a `.min(1)` that is really "this cannot be left empty". */
export const REQUIRED = 'This field is required'

/** For a checkbox the publisher has to tick to vouch for what they entered. */
export const CONFIRM_REQUIRED = 'Please confirm'

/** A confirmation field that does not match the value it confirms. */
export const CONFIRM_MISMATCH = 'Does not match the new password'

/**
 * Devise's `password_complexity` (`digit: 1, lower: 1, symbol: 1, upper: 1`) in one
 * sentence: it names all four classes, because the backend refuses on all four at once.
 */
export const PASSWORD_COMPLEXITY =
  'Add an upper-case letter, a lower-case one, a digit and a symbol'

/** The code an authenticator app shows — always the same length, always digits. */
export const enterCode = (digits: number) =>
  `Enter the ${digits} digits the app is showing`

export const chooseAtLeastOne = (what: string) => `Choose at least one ${what}`

/**
 * For a whole-object rule that says "fill in at least one of these" — raised through
 * `issue()` on the object, not on a field.
 */
export const AT_LEAST_ONE_FIELD = 'At least one field must be selected'

/** Pass a sum as `formatMoney(...)`, so the error reads like the hint beside the field. */
export const notLessThan = (n: number | string) => `Not less than ${n}`

export const notMoreThan = (n: number | string) => `Not more than ${n}`

export const betweenChars = (min: number, max: number) =>
  `Between ${min} and ${max} characters`

/**
 * For a field left empty or filled wrong, named by what belongs in it. Pass the noun with
 * its article — `enter('a CSS selector')`, `choose('the start date')` — so no a/an rule
 * has to guess.
 *
 * "Choose", never "Select", for anything picked rather than typed: the placeholder on the
 * control says "Choose …" too, and the message has to read as the same instruction.
 */
export const enter = (what: string) => `Enter ${what}`

export const choose = (what: string) => `Choose ${what}`

export const mustContain = (what: string) => `Must contain ${what}`

/** The one wording for "this is not a domain", whichever form asks for one. */
export const ENTER_DOMAIN = enter('a domain, for example example.com')

/**
 * The same for a link. The scheme is the part people leave out, and every backend that
 * takes one refuses it without — so the message names it rather than saying "invalid URL".
 */
export const ENTER_LINK = enter(
  'a full link, starting with http:// or https://'
)

/** A date that has to be far enough in the past — an age, a minimum tenure. */
export const atLeastYearsAgo = (years: number) => `At least ${years} years ago`

export const withinLastYears = (years: number) =>
  `Within the last ${years} years`

export const END_BEFORE_START =
  'The end date cannot be earlier than the start date'

/**
 * A `ctx.addIssue` body: `ctx.addIssue({ ...issue(choose('a stream')), path:
 * ['stream'] })`.
 *
 * `as const`: without it TypeScript widens the code to `string`, and zod's `addIssue` takes
 * a literal union. The raw string is the v4 idiom — `z.ZodIssueCode` is deprecated.
 */
export const issue = (message: string) => ({
  code: 'custom' as const,
  message
})
